(function () {
  'use strict';
  const define = (name, constructor) => { if (!customElements.get(name)) customElements.define(name, constructor); };
  const motionPreference = () => window.matchMedia('(prefers-reduced-motion: reduce)');
  const contextUrl = href => window.LehaCardPricing ? window.LehaCardPricing.contextUrl(href).href : href;

  define('leha-benefits', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.viewport = this.querySelector('[data-benefits-viewport]');
      this.track = this.querySelector('[data-benefits-track]');
      this.original = this.querySelector('[data-benefits-original]');
      this.button = this.querySelector('[data-benefits-pause]');
      if (!this.viewport || !this.original || !this.button) return;
      this.items = Array.from(this.original.children).filter(item => !item.hasAttribute('data-benefits-fill'));
      this.paused = this.dataset.paused === 'true';
      this.motion = motionPreference();
      const state = () => {
        this.dataset.paused = String(this.paused);
        this.dataset.reduced = String(this.motion.matches);
        this.button.disabled = this.motion.matches;
        this.button.setAttribute('aria-pressed', String(this.paused || this.motion.matches));
        const label = this.motion.matches ? this.button.dataset.reduced : this.paused ? this.button.dataset.resume : this.button.dataset.pause;
        this.querySelector('[data-benefits-label]').textContent = label;
        this.button.title = label;
        this.querySelector('[data-benefits-symbol]').textContent = this.paused || this.motion.matches ? '▷' : 'Ⅱ';
      };
      this.button.hidden = false;
      state();
      this.button.addEventListener('click', () => { this.paused = !this.paused; state(); }, { signal });
      const build = () => {
        if (signal.aborted || !this.isConnected) return;
        this.track.querySelector('[data-benefits-copy]')?.remove();
        this.original.querySelectorAll('[data-benefits-fill]').forEach(item => item.remove());
        const width = this.viewport.clientWidth;
        const stripEditorAttributes = element => {
          element.removeAttribute('data-shopify-editor-block');
          element.removeAttribute('data-shopify-editor-setting');
        };
        let rounds = 0;
        while (!this.motion.matches && this.original.getBoundingClientRect().width > 0 && this.original.getBoundingClientRect().width < width && rounds < 20) {
          this.items.forEach(item => {
            const fill = item.cloneNode(true);
            fill.setAttribute('aria-hidden', 'true');
            fill.setAttribute('data-benefits-fill', '');
            stripEditorAttributes(fill);
            this.original.append(fill);
          });
          rounds += 1;
        }
        const copy = this.original.cloneNode(true);
        copy.removeAttribute('data-benefits-original');
        copy.removeAttribute('aria-labelledby');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-benefits-copy', '');
        Array.from(copy.children).forEach(stripEditorAttributes);
        this.track.append(copy);
        this.dataset.ready = 'true';
      };
      this.motion.addEventListener('change', () => { state(); build(); }, { signal });
      build();
      if (window.ResizeObserver) {
        let previousWidth = this.viewport.clientWidth;
        this.observer = new ResizeObserver(() => {
          const width = this.viewport.clientWidth;
          if (width !== previousWidth) { previousWidth = width; build(); }
        });
        this.observer.observe(this.viewport);
      }
      document.fonts?.ready.then(build);
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  });

  define('leha-collection-shelf', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.tabs = Array.from(this.querySelectorAll('[data-collection-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-collection-panel]'));
      this.motion = motionPreference();
      this.rtl = (getComputedStyle(this).direction || this.closest('[dir]')?.getAttribute('dir')) === 'rtl';
      this.storageKey = 'leha-home-collection:' + this.dataset.sectionId;
      if (!this.tabs.length) return;
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-collection-tab]');
        if (tab && this.contains(tab) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.select(tab, true);
        }
        const button = event.target.closest('[data-carousel-step]');
        if (button && !button.disabled && this.contains(button)) this.move(button.closest('[data-collection-panel]'), Number(button.dataset.carouselStep));
      }, { signal });
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-collection-tab]');
        if (tab && this.tabs.includes(tab)) {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (event.key === 'ArrowRight') next = (index + (this.rtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + (this.rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
          if (next !== undefined) { event.preventDefault(); this.select(this.tabs[next], true); }
          if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this.select(tab, true); }
          return;
        }
        const track = event.target.closest('[data-product-track]');
        if (!track || event.target !== track) return;
        const panel = track.closest('[data-collection-panel]');
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(panel, (event.key === 'ArrowLeft') === this.rtl ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.move(panel, 0, event.key === 'Home' ? 0 : track.children.length - 1);
        }
      }, { signal });
      this.panels.forEach(panel => {
        const track = panel.querySelector('[data-product-track]');
        track.addEventListener('scroll', () => this.updateControls(panel), { passive:true, signal });
        panel.querySelector('[data-carousel-controls]').hidden = track.children.length < 2;
      });
      let initial = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (!window.Shopify?.designMode) {
        try { initial = this.tabs.find(tab => tab.dataset.collectionTab === sessionStorage.getItem(this.storageKey)) || initial; } catch (_) {}
      }
      this.select(initial, false);
      const scheduleMeasure = () => {
        if (this.frame || signal.aborted) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; if (!signal.aborted) this.reserveHeight(); });
      };
      if (window.ResizeObserver) {
        let previousWidth;
        this.observer = new ResizeObserver(() => {
          const width = this.clientWidth;
          if (width !== previousWidth) { previousWidth = width; scheduleMeasure(); }
        });
        this.observer.observe(this);
      }
      this.contentObserver = new MutationObserver(scheduleMeasure);
      this.contentObserver.observe(this.querySelector('.leha-home-shelf__panels'), { childList:true, subtree:true });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const tab = event.target.closest('[data-collection-tab]');
        if (tab) this.select(tab, false);
      }, { signal });
      document.fonts?.ready.then(scheduleMeasure);
      scheduleMeasure();
    }
    select(tab, focus) {
      this.tabs.forEach(item => { const active = item === tab; item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.collectionPanel !== tab.dataset.collectionTab; });
      const viewAll = this.querySelector('[data-collection-view-all]');
      viewAll.href = contextUrl(tab.href);
      viewAll.setAttribute('aria-label', this.dataset.viewLabel.replace('__collection__', tab.textContent.trim()));
      this.dataset.activeCollection = tab.dataset.collectionTab;
      if (focus) {
        tab.focus({ preventScroll:true });
        const list = tab.closest('[role="tablist"]');
        const bounds = list.getBoundingClientRect(), item = tab.getBoundingClientRect();
        if (item.left < bounds.left) list.scrollBy({ left:item.left - bounds.left, behavior:'auto' });
        else if (item.right > bounds.right) list.scrollBy({ left:item.right - bounds.right, behavior:'auto' });
        this.querySelector('[data-collection-status]').textContent = tab.textContent.trim();
        if (!window.Shopify?.designMode) { try { sessionStorage.setItem(this.storageKey, tab.dataset.collectionTab); } catch (_) {} }
      }
      this.updateControls(this.panels.find(panel => !panel.hidden));
    }
    reserveHeight() {
      let height = 0;
      this.panels.forEach(panel => {
        const hidden = panel.hidden;
        const style = panel.getAttribute('style');
        panel.style.position = 'absolute';
        panel.style.insetInline = '0';
        panel.style.visibility = 'hidden';
        panel.hidden = false;
        height = Math.max(height, panel.getBoundingClientRect().height);
        panel.hidden = hidden;
        if (style === null) panel.removeAttribute('style'); else panel.setAttribute('style', style);
      });
      this.querySelector('.leha-home-shelf__panels').style.minHeight = Math.ceil(height) + 'px';
      this.updateControls(this.panels.find(panel => !panel.hidden));
    }
    updateControls(panel) {
      if (!panel) return;
      const track = panel.querySelector('[data-product-track]');
      const cards = Array.from(track.children);
      const previous = panel.querySelector('[data-carousel-step="-1"]'), next = panel.querySelector('[data-carousel-step="1"]');
      if (!cards.length) { previous.disabled = next.disabled = true; return; }
      const bounds = track.getBoundingClientRect(), first = cards[0].getBoundingClientRect(), last = cards[cards.length - 1].getBoundingClientRect();
      previous.disabled = this.rtl ? first.right <= bounds.right + 5 : first.left >= bounds.left - 5;
      next.disabled = this.rtl ? last.left >= bounds.left - 5 : last.right <= bounds.right + 5;
    }
    move(panel, step, targetIndex) {
      const track = panel.querySelector('[data-product-track]'), cards = Array.from(track.children);
      if (!cards.length) return;
      const bounds = track.getBoundingClientRect();
      const edge = this.rtl ? 'right' : 'left';
      let current = 0, distance = Infinity;
      cards.forEach((card, index) => { const value = Math.abs(card.getBoundingClientRect()[edge] - bounds[edge]); if (value < distance) { current = index; distance = value; } });
      const index = targetIndex === undefined ? Math.min(cards.length - 1, Math.max(0, current + step)) : targetIndex;
      const delta = cards[index].getBoundingClientRect()[edge] - bounds[edge];
      track.scrollBy({ left:delta, behavior:this.motion.matches ? 'auto' : 'smooth' });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.contentObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });

  define('leha-home-scroll-header', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.root = this.closest('.shopify-section');
      this.navigation = this.root?.querySelector('leha-site-navigation[data-homepage="true"]');
      if (!this.navigation || window.Shopify?.designMode) return;
      this.root.classList.add('leha-home-scroll-header');
      this.lastY = Math.max(0, window.scrollY);
      this.travel = 0;
      this.direction = 0;
      const measure = () => {
        this.height = this.root.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--leha-home-header-clearance', Math.ceil(this.height + 16) + 'px');
      };
      measure();
      if (window.ResizeObserver) { this.observer = new ResizeObserver(measure); this.observer.observe(this.root); }
      const pinned = () => this.root.contains(document.activeElement) || Boolean(this.root.querySelector('details[open], [aria-expanded="true"], .cart-notification.active'));
      const coversFocus = () => {
        const active = document.activeElement;
        if (!active || active === document.body || this.root.contains(active)) return false;
        const bounds = active.getBoundingClientRect();
        return bounds.top >= 0 && bounds.top < this.height && bounds.bottom > 0;
      };
      const update = () => {
        this.frame = null;
        const y = Math.max(0, window.scrollY), delta = y - this.lastY;
        this.lastY = y;
        if (y <= this.height + 40 || pinned()) {
          this.root.dataset.headerHidden = 'false'; this.travel = 0; return;
        }
        if (Math.abs(delta) < 1) return;
        const direction = Math.sign(delta);
        this.travel = this.direction === direction ? this.travel + Math.abs(delta) : Math.abs(delta);
        this.direction = direction;
        if (direction > 0 && this.travel >= 24) this.root.dataset.headerHidden = 'true';
        if (direction < 0 && this.travel >= 12 && !coversFocus()) this.root.dataset.headerHidden = 'false';
      };
      this.root.dataset.headerHidden = 'false';
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, { passive:true, signal });
      window.addEventListener('pageshow', () => { this.root.dataset.headerHidden = 'false'; this.lastY = Math.max(0, window.scrollY); this.travel = 0; }, { signal });
      this.root.addEventListener('focusin', () => { this.root.dataset.headerHidden = 'false'; this.travel = 0; }, { signal });
      this.root.addEventListener('toggle', () => { if (pinned()) this.root.dataset.headerHidden = 'false'; }, { capture:true, signal });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.root?.classList.remove('leha-home-scroll-header');
      if (this.root) delete this.root.dataset.headerHidden;
      document.documentElement.style.removeProperty('--leha-home-header-clearance');
    }
  });
}());
